import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Activity, AlertCircle, CircleDashed, ServerCog, Clock, KeyRound, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { toast } from 'sonner';
import { timeAgo } from '@/utils/cn';
import { InstallTokenModal } from './InstallTokenModal';

type AgentHealth = 'online' | 'stale' | 'offline' | 'never_seen';

interface AgentRow {
  id: string;
  machine_id: string;
  hostname?: string | null;
  os?: string | null;
  os_version?: string | null;
  agent_version?: string | null;
  status: 'pending' | 'active' | 'revoked';
  last_seen_at?: string | null;
  last_error_at?: string | null;
  last_error_message?: string | null;
  consecutive_errors: number;
  registered_at?: string | null;
  health: AgentHealth;
}

const HEALTH_STYLES: Record<AgentHealth, { label: string; tone: 'green' | 'amber' | 'red' | 'gray'; icon: React.ReactNode }> = {
  online:     { label: 'Online',      tone: 'green', icon: <Activity className="w-3 h-3" /> },
  stale:      { label: 'Sem sinal',   tone: 'amber', icon: <Clock className="w-3 h-3" /> },
  offline:    { label: 'Offline',     tone: 'red',   icon: <AlertCircle className="w-3 h-3" /> },
  never_seen: { label: 'Nunca visto', tone: 'gray',  icon: <CircleDashed className="w-3 h-3" /> },
};

function HealthBadge({ health }: { health: AgentHealth }) {
  const s = HEALTH_STYLES[health];
  const toneClass = {
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    red:   'bg-red-100 text-red-700 border-red-200',
    gray:  'bg-zinc-100 text-zinc-600 border-zinc-200',
  }[s.tone];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${toneClass}`}>
      {s.icon}{s.label}
    </span>
  );
}

export function AgentsListPage() {
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [recoverAgentId, setRecoverAgentId] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-etl', 'agents'],
    queryFn: () => apiClient.agentEtl.listAgents().then(r => r.data.agents as AgentRow[]),
    refetchInterval: 15_000, // auto-refresh para health acompanhar
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiClient.agentEtl.revokeAgent(id),
    onSuccess: () => {
      toast.success('Agente revogado.');
      qc.invalidateQueries({ queryKey: ['agent-etl', 'agents'] });
    },
    onError: () => toast.error('Falha ao revogar agente.'),
  });

  const del = useMutation({
    mutationFn: (id: string) => apiClient.agentEtl.deleteAgent(id),
    onSuccess: () => {
      toast.success('Agente removido.');
      qc.invalidateQueries({ queryKey: ['agent-etl', 'agents'] });
    },
    onError: (err: any) => {
      // 404 = agent já não existe (foi deletado em outra aba/sessão). Trata como sucesso.
      if (err?.response?.status === 404) {
        toast.info('Agente já não existia. Lista atualizada.');
        qc.invalidateQueries({ queryKey: ['agent-etl', 'agents'] });
        return;
      }
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Falha ao remover.';
      toast.error(msg);
    },
  });

  const pending = data?.filter(a => a.status === 'pending') ?? [];
  const active  = data?.filter(a => a.status !== 'pending') ?? [];

  return (
    <div className="container-app py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ServerCog className="w-6 h-6" /> Agentes ETL
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Conectores instalados no servidor/caixa do mercado que sincronizam produtos do ERP.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/settings/agent/routines">
            <Button variant="secondary">Rotinas</Button>
          </Link>
          <Link to="/settings/agent/logs">
            <Button variant="secondary">Logs</Button>
          </Link>
          <Button onClick={() => setShowInstallModal(true)}>
            <Plus className="w-4 h-4 mr-1" /> Gerar token de instalação
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 border border-red-200 bg-red-50 rounded-lg text-sm text-red-700">
          Falha ao carregar agentes. Recarregue a página.
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando agentes…</div>
      ) : !data?.length ? (
        <EmptyState onClick={() => setShowInstallModal(true)} />
      ) : (
        <>
          {/* Pendings em destaque no topo */}
          {pending.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> Aguardando instalação ({pending.length})
              </div>
              {pending.map(a => (
                <div key={a.id} className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{a.hostname || '(sem identificação)'}</span>
                        <Badge variant="outline">pendente registro</Badge>
                      </div>
                      <p className="text-xs text-amber-800 mt-1">
                        Token gerado, mas o agente ainda não foi instalado/registrado.
                        Token expira em 24h após a geração.
                      </p>
                      <div className="text-[10px] text-zinc-500 mt-1 font-mono truncate">
                        id: {a.id}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" onClick={() => setRecoverAgentId(a.id)}>
                        <KeyRound className="w-3 h-3 mr-1" /> Ver instruções
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm('Cancelar este agente pendente? O token será invalidado.'))
                            del.mutate(a.id);
                        }}
                        disabled={del.isPending}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Active + revoked */}
          {active.length > 0 && (
            <div className="grid gap-3">
              {pending.length > 0 && (
                <div className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">
                  Agentes registrados ({active.length})
                </div>
              )}
              {active.map(a => (
                <div key={a.id} className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{a.hostname || '(sem hostname)'}</span>
                        <HealthBadge health={a.health} />
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-violet-50 text-violet-700 border-violet-200">
                          <ServerCog className="w-3 h-3" /> v{a.agent_version || '?'}
                        </span>
                        {a.status === 'revoked' && <Badge variant="outline">revogado</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                        <span>OS: {a.os || '—'} {a.os_version || ''}</span>
                        <span>Último sinal: {a.last_seen_at ? timeAgo(a.last_seen_at) : '—'}</span>
                        <span>Erros consecutivos: {a.consecutive_errors}</span>
                      </div>
                      <div className="text-xs text-zinc-400 mt-1 font-mono truncate">
                        machine_id: {a.machine_id}
                      </div>
                      {a.last_error_message && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">
                          Último erro: {a.last_error_message}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {a.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Revogar agente "${a.hostname || a.machine_id}"? O agente continuará rodando localmente mas não conseguirá mais conectar.`))
                              revoke.mutate(a.id);
                          }}
                          disabled={revoke.isPending}
                        >
                          Revogar
                        </Button>
                      )}
                      {a.status === 'revoked' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Remover permanentemente o agente "${a.hostname || a.machine_id}"?`))
                              del.mutate(a.id);
                          }}
                          disabled={del.isPending}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Remover
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showInstallModal && (
        <InstallTokenModal
          onClose={() => {
            setShowInstallModal(false);
            qc.invalidateQueries({ queryKey: ['agent-etl', 'agents'] });
          }}
        />
      )}
      {recoverAgentId && (
        <InstallTokenModal
          recoverAgentId={recoverAgentId}
          onClose={() => {
            setRecoverAgentId(null);
            qc.invalidateQueries({ queryKey: ['agent-etl', 'agents'] });
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ onClick }: { onClick: () => void }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-zinc-200 p-12 text-center">
      <ServerCog className="w-12 h-12 mx-auto text-zinc-300 mb-3" />
      <h2 className="text-lg font-medium mb-1">Nenhum agente ETL registrado</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Instale o MyShow - Agente no servidor/caixa onde roda o ERP do cliente para sincronizar produtos automaticamente.
      </p>
      <Button onClick={onClick}>
        <Plus className="w-4 h-4 mr-1" /> Gerar token de instalação
      </Button>
    </div>
  );
}
